import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import belowIcon from '../../asset/icon/below.png';
import deleteIcon from '../../asset/icon/delete.png';
import {
  BlackFullWidthBtn,
  PaperBlurFitBtn,
} from '../../shared/component/headless/button/BtnBody';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { ChangeModalFrame } from '../../shared/component/modal/ChangeModalFrame';
import SelectBox from '../../shared/component/SelectBox';
import { UniversalCheckbox } from '../../shared/component/UniversalCheckbox';
import { getBtnName } from '../../shared/constants/btnNames';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getPageMessage } from '../../shared/constants/pageMessages';
import {
  TextImportCandidateData,
  TextImportCandidatePageData,
  TextImportSourceData,
} from '../../shared/dto/EntityRepository';
import {
  AlertPosition,
  AlertType,
  MemberType,
  TextImportSourceAvailability,
} from '../../shared/enum/EnumRepository';
import { useApprovalRequestActions } from '../../shared/hooks/useApi/useApprovalRequest';
import { getTextImportCandidates, getTextImportSources } from '../../shared/api/textImportApi';
import { useAuthStore } from '../../shared/store/authStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useSettingStore } from '../../shared/store/settingStore';

type LoadStatus = 'idle' | 'loading' | 'success' | 'error';

interface TextCopyImportModalProps {
  isMobile?: boolean;
}

const getSourceLabel = (source: TextImportSourceData): string => {
  const groupMemberName = source.groupMemberName?.trim();
  const username = source.username?.trim();
  const handle = source.handle?.trim();
  const accountName = username || (handle ? `@${handle}` : source.aui);
  return groupMemberName && groupMemberName !== accountName
    ? `${groupMemberName} · ${accountName}${handle && username ? ` (@${handle})` : ''}`
    : `${accountName}${handle && username ? ` (@${handle})` : ''}`;
};

const getCandidateTitle = (candidate: TextImportCandidateData): string =>
  candidate.title?.trim()
  || candidate.source?.title?.trim()
  || getPageMessage('TEXT_COPY_IMPORT_UNTITLED');

const getCandidateMeta = (candidate: TextImportCandidateData): string => [
  candidate.source?.author?.trim(),
  candidate.source?.title?.trim(),
  candidate.subtitle?.trim(),
  candidate.prodYear,
].filter((value) => value !== null && value !== undefined && value !== '').join(' · ');

const getSourceAvailabilityMessage = (
  availability: TextImportSourceAvailability,
): string => {
  switch (availability) {
    case TextImportSourceAvailability.GROUP_MEMBER_INACTIVE:
      return getPageMessage('TEXT_COPY_IMPORT_GROUP_MEMBER_INACTIVE');
    case TextImportSourceAvailability.ACCOUNT_INACTIVE:
      return getPageMessage('TEXT_COPY_IMPORT_ACCOUNT_INACTIVE');
    case TextImportSourceAvailability.ACCOUNT_PRIVATE:
      return getPageMessage('TEXT_COPY_IMPORT_ACCOUNT_PRIVATE');
    case TextImportSourceAvailability.TEXTS_PRIVATE:
      return getPageMessage('TEXT_COPY_IMPORT_TEXTS_PRIVATE');
    default:
      return getPageMessage('TEXT_COPY_IMPORT_SOURCE_UNAVAILABLE');
  }
};

const TextCopyImportModal: React.FC<TextCopyImportModalProps> = ({
  isMobile = false,
}) => {
  const accountAui = useAuthStore((state) => state.user?.aui);
  const memberType = useSettingStore((state) => state.setting?.memberType);
  const { isClosing, clearModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();
  const { createTextCopyApprovalRequest } = useApprovalRequestActions();

  const [sources, setSources] = useState<TextImportSourceData[]>([]);
  const [sourceStatus, setSourceStatus] = useState<LoadStatus>('idle');
  const [sourceReloadKey, setSourceReloadKey] = useState(0);
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
  const [candidatePage, setCandidatePage] = useState<TextImportCandidatePageData | null>(null);
  const [candidateStatus, setCandidateStatus] = useState<LoadStatus>('idle');
  const [candidateReloadKey, setCandidateReloadKey] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedTextIds, setSelectedTextIds] = useState<Set<number>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const close = useCallback(
    () => clearModal(isMobile ? 400 : 300),
    [clearModal, isMobile],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [close]);

  useEffect(() => {
    if (!accountAui || memberType !== MemberType.GROUP) return;
    let active = true;
    setSourceStatus('loading');
    setSelectedTextIds(new Set());
    setCandidatePage(null);
    setPage(1);

    void getTextImportSources(accountAui)
      .then((response) => {
        if (!active) return;
        const nextSources = response.data;
        setSources(nextSources);
        setSelectedSourceId((current) =>
          nextSources.some((source) => source.groupMemberId === current)
            ? current
            : nextSources[0]?.groupMemberId ?? null);
        setSourceStatus('success');
      })
      .catch(() => {
        if (!active) return;
        setSources([]);
        setSelectedSourceId(null);
        setSourceStatus('error');
      });

    return () => {
      active = false;
    };
  }, [accountAui, memberType, sourceReloadKey]);

  const selectedSource = sources.find(
    (source) => source.groupMemberId === selectedSourceId,
  );

  useEffect(() => {
    if (
      !accountAui
      || selectedSourceId === null
      || selectedSource?.availability !== TextImportSourceAvailability.AVAILABLE
    ) {
      setCandidatePage(null);
      setCandidateStatus('idle');
      return;
    }

    let active = true;
    setCandidateStatus('loading');
    setCandidatePage(null);
    void getTextImportCandidates(accountAui, selectedSourceId, page)
      .then((response) => {
        if (!active) return;
        if (response.data.totalPages > 0 && page > response.data.totalPages) {
          setPage(response.data.totalPages);
          return;
        }
        setCandidatePage(response.data);
        setSelectedTextIds((current) => {
          const pendingIds = new Set(
            response.data.content
              .filter((candidate) => candidate.pendingApproval)
              .map((candidate) => candidate.id),
          );
          if (![...current].some((id) => pendingIds.has(id))) return current;
          return new Set([...current].filter((id) => !pendingIds.has(id)));
        });
        setCandidateStatus('success');
      })
      .catch(() => {
        if (!active) return;
        setCandidateStatus('error');
      });

    return () => {
      active = false;
    };
  }, [accountAui, selectedSourceId, selectedSource?.availability, page, candidateReloadKey]);

  const sourceById = useMemo(
    () => new Map(sources.map((source) => [source.groupMemberId, source])),
    [sources],
  );

  const getSourceDisplayText = (groupMemberId: number): string => {
    const source = sourceById.get(groupMemberId);
    return source ? getSourceLabel(source) : '';
  };

  const handleSourceChange = (groupMemberId: number) => {
    if (groupMemberId === selectedSourceId) return;

    const changeSource = () => {
      setSelectedSourceId(groupMemberId);
      setSelectedTextIds(new Set());
      setCandidatePage(null);
      setPage(1);
    };

    if (selectedTextIds.size === 0) {
      changeSource();
      return;
    }

    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_CHANGE_TEXT_IMPORT_ACCOUNT'),
      callBack: changeSource,
    });
  };

  const handleCandidateChange = (candidate: TextImportCandidateData) => {
    if (candidate.pendingApproval) return;
    if (!selectedTextIds.has(candidate.id) && selectedTextIds.size >= 10) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('BULK_IMPORT_MAX_EXCEEDED', { maxCount: 10 }),
      });
      return;
    }
    setSelectedTextIds((current) => {
      const next = new Set(current);
      if (next.has(candidate.id)) {
        next.delete(candidate.id);
      } else {
        next.add(candidate.id);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (selectedSourceId === null || selectedTextIds.size === 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TEXT_COPY_EMPTY'),
      });
      return;
    }
    setIsSubmitting(true);
    const sent = await createTextCopyApprovalRequest({
      groupMemberId: selectedSourceId,
      textIds: [...selectedTextIds],
    });
    if (sent) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getPageMessage('TEXT_COPY_IMPORT_SENT'),
      });
      close();
      return;
    }
    setCandidateReloadKey((current) => current + 1);
    setIsSubmitting(false);
  };

  if (!accountAui || memberType !== MemberType.GROUP) return null;

  const renderCandidates = () => {
    if (
      selectedSource
      && selectedSource.availability !== TextImportSourceAvailability.AVAILABLE
    ) {
      return (
        <StatusArea>
          {getSourceAvailabilityMessage(selectedSource.availability)}
        </StatusArea>
      );
    }
    if (candidateStatus === 'idle' || candidateStatus === 'loading') {
      return <StatusArea>{getPageMessage('TEXT_COPY_IMPORT_TEXT_LOADING')}</StatusArea>;
    }
    if (candidateStatus === 'error') {
      return (
        <StatusArea role="alert">
          <span>{getPageMessage('TEXT_COPY_IMPORT_TEXT_FAILED')}</span>
          <RetryButton onClick={() => setCandidateReloadKey((current) => current + 1)}>
            {getBtnName('RETRY')}
          </RetryButton>
        </StatusArea>
      );
    }
    if (!candidatePage || candidatePage.content.length === 0) {
      return <StatusArea>{getPageMessage('TEXT_COPY_IMPORT_TEXT_EMPTY')}</StatusArea>;
    }

    return (
      <CandidateList>
        {candidatePage.content.map((candidate) => {
          const disabled = candidate.pendingApproval;
          const meta = getCandidateMeta(candidate);
          return (
            <CandidateRow key={candidate.id} $disabled={disabled}>
              <CandidateCheckbox
                checked={selectedTextIds.has(candidate.id)}
                disabled={disabled}
                onChange={() => handleCandidateChange(candidate)}
              />
              <CandidateContent>
                <CandidateTitle>{getCandidateTitle(candidate)}</CandidateTitle>
                {meta && <CandidateMeta>{meta}</CandidateMeta>}
              </CandidateContent>
              {disabled && (
                <PendingLabel>{getPageMessage('TEXT_COPY_IMPORT_PENDING')}</PendingLabel>
              )}
            </CandidateRow>
          );
        })}
      </CandidateList>
    );
  };

  const body = (
    <ModalBody $isMobile={isMobile}>
      <Header $isMobile={isMobile}>
        <TitleGroup>
          <Title $isMobile={isMobile}>{getPageMessage('TEXT_COPY_IMPORT_TITLE')}</Title>
          <SelectedCount>
            {getPageMessage('TEXT_COPY_IMPORT_SELECTED', { count: selectedTextIds.size })}
          </SelectedCount>
        </TitleGroup>
        <UniversalIconBtn
          size={isMobile ? 'fit' : 'small'}
          border={false}
          isTransparent
          hoverOpacity={!isMobile}
          backgroundImg={isMobile ? belowIcon : deleteIcon}
          onClick={close}
          aria-label="Close"
        />
      </Header>
      <Explain>{getPageMessage('TEXT_COPY_IMPORT_EXPLAIN')}</Explain>

      {sourceStatus === 'idle' || sourceStatus === 'loading' ? (
        <StatusArea>{getPageMessage('TEXT_COPY_IMPORT_SOURCE_LOADING')}</StatusArea>
      ) : sourceStatus === 'error' ? (
        <StatusArea role="alert">
          <span>{getPageMessage('TEXT_COPY_IMPORT_SOURCE_FAILED')}</span>
          <RetryButton onClick={() => setSourceReloadKey((current) => current + 1)}>
            {getBtnName('RETRY')}
          </RetryButton>
        </StatusArea>
      ) : sources.length === 0 || selectedSourceId === null ? (
        <StatusArea>{getPageMessage('TEXT_COPY_IMPORT_SOURCE_EMPTY')}</StatusArea>
      ) : (
        <ImportContent>
          <AccountField>
            <FieldLabel>{getPageMessage('TEXT_COPY_IMPORT_ACCOUNT_LABEL')}</FieldLabel>
            <AccountSelectBoxWrapper>
              <SelectBox
                value={selectedSourceId}
                options={sources.map((source) => source.groupMemberId)}
                handleChange={handleSourceChange}
                direction={false}
                getDisplayText={getSourceDisplayText}
              />
            </AccountSelectBoxWrapper>
          </AccountField>
          <CandidateArea>{renderCandidates()}</CandidateArea>
          {candidatePage && candidatePage.totalPages > 1 && (
            <Pagination aria-label="Text pages">
              <PageButton
                disabled={page <= 1 || candidateStatus === 'loading'}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                aria-label="Previous page"
              >
                ←
              </PageButton>
              <PageStatus>{candidatePage.page} / {candidatePage.totalPages}</PageStatus>
              <PageButton
                disabled={page >= candidatePage.totalPages || candidateStatus === 'loading'}
                onClick={() => setPage((current) => current + 1)}
                aria-label="Next page"
              >
                →
              </PageButton>
            </Pagination>
          )}
          <RequestButton onClick={() => void handleSubmit()}>
            {getPageMessage(
              isSubmitting ? 'TEXT_COPY_IMPORT_REQUESTING' : 'TEXT_COPY_IMPORT_REQUEST',
            )}
          </RequestButton>
        </ImportContent>
      )}
    </ModalBody>
  );

  if (isMobile) return <MobileFrame>{body}</MobileFrame>;
  return (
    <ImportModalFrame $isClosing={isClosing} onClick={(event) => event.stopPropagation()}>
      {body}
    </ImportModalFrame>
  );
};

const ImportModalFrame = styled(ChangeModalFrame)`
  width: min(540px, calc(100vw - 80px));
  height: min(720px, 82vh);
  box-sizing: border-box;
  overflow: hidden;
`;

const MobileFrame = styled.div`
  width: 100%;
  height: min(760px, calc(100dvh - 40px));
  min-height: 0;
  padding-bottom: 20px;
  box-sizing: border-box;
  overflow: hidden;
`;

const ModalBody = styled.div<{ $isMobile: boolean }>`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: ${({ $isMobile }) => ($isMobile ? '0' : '0.5vw')};
  box-sizing: border-box;
`;

const Header = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: ${({ $isMobile }) => ($isMobile ? 'clamp(12px, 2vh, 30px)' : '0')};
  margin-bottom: ${({ $isMobile }) => ($isMobile ? 'clamp(20px, 5vh, 30px)' : '3vh')};
`;

const Title = styled.h2<{ $isMobile: boolean }>`
  min-width: 0;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme, $isMobile }) => ($isMobile
    ? theme.typography.Body_01_1
    : theme.typography.Body_02_1)};
`;

const TitleGroup = styled.div`
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 12px;
`;

const Explain = styled.p`
  flex-shrink: 0;
  margin-bottom: 20px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  white-space: pre-line;
  ${({ theme }) => theme.typography.Body_04};
`;

const ImportContent = styled.div`
  min-height: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const AccountField = styled.div`
  flex-shrink: 0;
  margin-bottom: 18px;
`;

const AccountSelectBoxWrapper = styled.div`
  width: clamp(220px, 32vw, 380px);
  max-width: 100%;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_04};
`;

const FieldLabel = styled.div`
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const CandidateArea = styled.div`
  min-height: 0;
  display: flex;
  flex: 1;
  overflow: hidden;
  border-top: 0.5px solid ${({ theme }) => theme.colors.color_Gray_06};
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_06};
`;

const CandidateList = styled.div`
  width: 100%;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CandidateRow = styled.label<{ $disabled: boolean }>`
  width: 100%;
  min-height: 48px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 2px;
  box-sizing: border-box;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_06};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
`;

const CandidateCheckbox = styled(UniversalCheckbox)`
  flex-shrink: 0;
  margin-left: 2px;

  &:disabled {
    cursor: default;
  }
`;

const CandidateContent = styled.div`
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
`;

const CandidateTitle = styled.div`
  overflow: hidden;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Body_04};
`;

const CandidateMeta = styled.div`
  overflow: hidden;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Body_04};
`;

const PendingLabel = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const StatusArea = styled.div`
  min-height: 160px;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  text-align: center;
  white-space: pre-line;
  ${({ theme }) => theme.typography.Body_04};
`;

const Pagination = styled.nav`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 10px 0 4px;
`;

const PageButton = styled.button`
  width: 28px;
  height: 28px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  background: transparent;
  border: none;

  &:disabled {
    opacity: 0.25;
    cursor: default;
  }
`;

const PageStatus = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const RequestButton = styled(BlackFullWidthBtn)`
  width: 100%;
  flex-shrink: 0;
  margin-top: 14px;
`;

const SelectedCount = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const RetryButton = styled(PaperBlurFitBtn)`
  padding: 8px 1rem;
`;

export default TextCopyImportModal;
